import React, { useState, useCallback } from "react";
import { Frame, Navigation, TopBar, Select } from "@shopify/polaris";
import {
  ProductIcon,
  EmailIcon,
  AppsIcon,
  SettingsIcon,
  PersonIcon,
  PaintBrushFlatIcon,
  ExitIcon,
  PageIcon,
  ChartVerticalIcon,
  StoreIcon,
} from "@shopify/polaris-icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useI18n } from "../context/I18nContext.jsx";
import { useShop } from "../context/ShopContext.jsx";
import { useAdminAuth } from "../context/AdminAuthContext.jsx";
import ProblemReportLink from './ProblemReportLink.jsx';

export default function AppLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const { shopDomain, shops, switchShop } = useShop();
  const { admin, logout } = useAdminAuth();
  const [userMenuActive, setUserMenuActive] = useState(false);

  const toggleUserMenu = useCallback(() => setUserMenuActive((v) => !v), []);

  const shopSwitcher = shops.length > 1 ? (
    <div style={{ padding: "12px 16px", borderBottom: "1px solid #e1e3e5" }}>
      <Select
        label={t("nav.activeShop")}
        labelInline
        options={shops.map((s) => ({
          label: s.shopDomain.replace(".myshopify.com", ""),
          value: s.shopDomain,
        }))}
        value={shopDomain || ""}
        onChange={switchShop}
      />
    </div>
  ) : null;

  const navigationMarkup = (
    <Navigation location={location.pathname}>
      {shopSwitcher && (
        <div>{shopSwitcher}</div>
      )}
      <Navigation.Section
        title={t("nav.appName")}
        items={[
          {
            label: t("nav.integration"),
            icon: SettingsIcon,
            url: "/integration",
            selected: location.pathname === "/integration",
            onClick: () => navigate("/integration"),
          },
          {
            label: t("nav.products"),
            icon: ProductIcon,
            url: "/products",
            selected: location.pathname.startsWith("/products"),
            onClick: () => navigate("/products"),
          },
          {
            label: t("nav.sizeCharts"),
            icon: ProductIcon,
            url: "/size-charts",
            selected: location.pathname === "/size-charts",
            onClick: () => navigate("/size-charts"),
          },
          {
            label: t("nav.showroom"),
            icon: StoreIcon,
            url: "/showroom-link",
            selected: location.pathname === "/showroom-link",
            onClick: () => navigate("/showroom-link"),
          },
          {
            label: t("nav.landingPageTemplate"),
            icon: PageIcon,
            url: "/landing-page-template",
            selected: location.pathname === "/landing-page-template",
            onClick: () => navigate("/landing-page-template"),
          },
          {
            label: t("nav.sessions"),
            icon: EmailIcon,
            url: "/sessions",
            selected: location.pathname === "/sessions",
            onClick: () => navigate("/sessions"),
          },
          {
            label: t("nav.analytics"),
            icon: ChartVerticalIcon,
            url: "/analytics",
            selected: location.pathname === "/analytics",
            onClick: () => navigate("/analytics"),
          },
          {
            label: t("nav.customers"),
            icon: PersonIcon,
            url: "/customers",
            selected: location.pathname === "/customers",
            onClick: () => navigate("/customers"),
          },
          {
            label: t("nav.branding"),
            icon: PaintBrushFlatIcon,
            url: "/branding",
            selected: location.pathname === "/branding",
            onClick: () => navigate("/branding"),
          },
          {
            label: t("nav.aiSettings"),
            icon: AppsIcon,
            url: "/ai-settings",
            selected: location.pathname === "/ai-settings",
            onClick: () => navigate("/ai-settings"),
          },
          {
            label: t("nav.settings"),
            icon: SettingsIcon,
            url: "/settings",
            selected: location.pathname === "/settings",
            onClick: () => navigate("/settings"),
          },
        ]}
      />
      <Navigation.Section
        title={t("nav.about")}
        items={[
          {
            label: t("nav.poweredBy"),
            icon: AppsIcon,
            disabled: true,
          },
        ]}
      />
      <div style={{ padding: '8px 28px', borderTop: '1px solid #e1e3e5' }}>
        <ProblemReportLink style={{ color: '#303030', fontSize: 14 }} />
      </div>
    </Navigation>
  );

  const userMenuMarkup = (
    <TopBar.UserMenu
      actions={[
        {
          items: [
            {
              content: t("nav.logout"),
              icon: ExitIcon,
              onAction: logout,
            },
          ],
        },
      ]}
      name={admin?.name || admin?.email || "Admin"}
      initials={(admin?.name?.[0] || admin?.email?.[0] || "A").toUpperCase()}
      open={userMenuActive}
      onToggle={toggleUserMenu}
    />
  );

  const topBarMarkup = (
    <TopBar
      showNavigationToggle
      userMenu={userMenuMarkup}
    />
  );

  return (
    <Frame navigation={navigationMarkup} topBar={topBarMarkup}>
      {children}
    </Frame>
  );
}
